import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Download, Trash2, Loader2, FileText, File, Link2, ExternalLink, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import FilePreviewDialog from './FilePreviewDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

// Using relative URLs
const API = '/api';

const FileList = ({ auditId, controlId, evidenceIndex, refreshTrigger = 0, compact = false, showEmpty = true, generalOnly = false }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(!!auditId);
  const [downloading, setDownloading] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (auditId) {
      fetchFiles();
    } else {
      setLoading(false);
      setFiles([]);
    }
  }, [auditId, controlId, evidenceIndex, refreshTrigger, generalOnly]);

  const fetchFiles = async () => {
    // Don't fetch if auditId is not provided
    if (!auditId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Build query params for filtering
      let url = `${API}/files/audit/${auditId}`;
      const params = new URLSearchParams();
      if (controlId) params.append('control_id', controlId);
      if (evidenceIndex !== undefined && evidenceIndex !== null) params.append('evidence_index', evidenceIndex.toString());
      if (generalOnly) params.append('general_only', 'true');
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(response.data);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      // Don't show error toast for 404 (no files found is normal)
      if (error.response?.status !== 404) {
        // Only show error for actual failures, not missing audit
        if (error.response?.status !== 403) {
          toast.error('Failed to load files');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Decide whether a file is sensible to "view" inline. Native browser
  // renderers handle PDF/image/text/audio/video; office docs are converted
  // to PDF server-side via /api/files/preview/{id} (LibreOffice in our own
  // pod, never leaves the platform).
  const canViewInline = (file) => {
    if (file?.kind === 'policy') return false; // policies use the ExternalLink action
    const mt = (file?.mime_type || '').toLowerCase();
    return (
      mt.startsWith('image/') ||
      mt.startsWith('video/') ||
      mt.startsWith('audio/') ||
      mt === 'application/pdf' ||
      mt.startsWith('text/') ||
      // Office formats — rendered server-side to PDF
      mt === 'application/msword' ||
      mt === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      mt === 'application/vnd.ms-excel' ||
      mt === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      mt === 'application/vnd.ms-powerpoint' ||
      mt === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      mt === 'application/rtf' ||
      mt === 'application/vnd.oasis.opendocument.text' ||
      mt === 'application/vnd.oasis.opendocument.spreadsheet' ||
      mt === 'application/vnd.oasis.opendocument.presentation'
    );
  };

  const handleView = async (file) => {
    // Policy attachments still route to the policy page — there's no real
    // file blob to preview. For real uploads we open the in-app modal.
    if (file?.kind === 'policy') {
      try {
        setViewing(file.id);
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/files/download/${file.id}?disposition=inline`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.kind === 'policy' && response.data?.policy_id) {
          navigate(`/policies/${response.data.policy_id}`);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || `Failed to open ${file?.filename}`);
      } finally {
        setViewing(null);
      }
      return;
    }
    // Open the in-app preview dialog. FilePreviewDialog handles the URL
    // fetch + iframe/img/video rendering.
    setPreviewFile(file);
  };

  const handleDownload = async (fileId, filename) => {
    try {
      setDownloading(fileId);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/files/download/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Linked policies are virtual files — route to the policy page instead
      // of trying to open a download URL that doesn't exist.
      if (response.data?.kind === 'policy' && response.data?.policy_id) {
        navigate(`/policies/${response.data.policy_id}`);
        toast.success(`Opening policy: ${response.data.policy_name || filename}`);
        return;
      }

      // Open download link in new tab
      window.open(response.data.download_url, '_blank');
      toast.success(`Downloading ${filename}`);
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error('Failed to download file');
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (fileId) => {
    try {
      setDeleting(fileId);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/files/delete/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(files.filter(f => f.id !== fileId));
      toast.success('File deleted successfully');
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast.error('Failed to delete file');
    } finally {
      setDeleting(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (file) => {
    if (file?.kind === 'policy') return <Link2 className="w-4 h-4 text-navy-600" />;
    const mimeType = file?.mime_type;
    if (mimeType?.includes('pdf')) return <FileText className="w-4 h-4 text-red-500" />;
    if (mimeType?.includes('image')) return <File className="w-4 h-4 text-blue-500" />;
    if (mimeType?.includes('video')) return <File className="w-4 h-4 text-purple-500" />;
    return <File className="w-4 h-4 text-slate-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        <span className="ml-2 text-sm text-slate-600">Loading files...</span>
      </div>
    );
  }

  if (files.length === 0) {
    if (!showEmpty) return null;
    return (
      <div className="text-center py-6 text-slate-500">
        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No files uploaded yet</p>
      </div>
    );
  }

  if (compact) {
    return (
      <>
      <div className="space-y-2">
        {files.map((file) => {
          const isPolicy = file.kind === 'policy';
          return (
          <div
            key={file.id}
            className={`flex items-center justify-between gap-2 p-2 border rounded-lg transition ${isPolicy ? 'border-navy-200 bg-navy-50/40 hover:bg-navy-50' : 'border-slate-200 hover:bg-slate-50'}`}
            data-testid={isPolicy ? `policy-evidence-${file.policy_id}` : undefined}
          >
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              {getFileIcon(file)}
              <span className="text-sm text-slate-900 truncate" title={file.filename}>{file.filename}</span>
              {isPolicy ? (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-navy-700 border-navy-200 flex-shrink-0">Policy</Badge>
              ) : (
                <span className="text-xs text-slate-500 flex-shrink-0 whitespace-nowrap">{formatFileSize(file.file_size)}</span>
              )}
              {file.generated_by_ai && (
                <Badge
                  variant="outline"
                  className="text-[10px] uppercase tracking-wide text-violet-700 border-violet-300 bg-violet-50 flex-shrink-0"
                  data-testid={`ai-evidence-badge-${file.id}`}
                  title={`AI-generated · ${file.ai_model || 'model'} · ${file.ai_artifact_type || ''}`}
                >
                  AI · {(file.ai_artifact_type || 'evidence').replace('_', ' ')}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {canViewInline(file) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleView(file)}
                  disabled={viewing === file.id}
                  title="View"
                  data-testid={`file-view-btn-${file.id}`}
                >
                  {viewing === file.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Eye className="w-3 h-3" />
                  )}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(file.id, file.filename)}
                disabled={downloading === file.id}
                title={isPolicy ? 'Open policy' : 'Download'}
              >
                {downloading === file.id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : isPolicy ? (
                  <ExternalLink className="w-3 h-3" />
                ) : (
                  <Download className="w-3 h-3" />
                )}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    disabled={deleting === file.id}
                  >
                    {deleting === file.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Trash2 className="w-3 h-3 text-red-600" />
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{isPolicy ? 'Unlink policy' : 'Delete File'}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {isPolicy
                        ? `Remove the link to "${file.filename}" from this evidence requirement? The policy itself won't be deleted.`
                        : `Are you sure you want to delete "${file.filename}"? This action cannot be undone.`}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDelete(file.id)}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {isPolicy ? 'Unlink' : 'Delete'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
          );
        })}
      </div>
      <FilePreviewDialog open={!!previewFile} file={previewFile} onClose={() => setPreviewFile(null)} />
      </>
    );
  }

  return (
    <>
    <div className="space-y-3">
      {files.map((file) => {
        const isPolicy = file.kind === 'policy';
        return (
        <div
          key={file.id}
          className={`flex items-center justify-between gap-3 p-4 border rounded-lg transition ${isPolicy ? 'border-navy-200 bg-navy-50/40 hover:bg-navy-50' : 'border-slate-200 hover:bg-slate-50'}`}
          data-testid={isPolicy ? `policy-evidence-${file.policy_id}` : undefined}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              {getFileIcon(file)}
              <p className="font-medium text-slate-900 truncate" title={file.filename}>{file.filename}</p>
              {isPolicy && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-navy-700 border-navy-200 flex-shrink-0">Linked policy</Badge>
              )}
              {file.generated_by_ai && (
                <Badge
                  variant="outline"
                  className="text-[10px] uppercase tracking-wide text-violet-700 border-violet-300 bg-violet-50 flex-shrink-0"
                  data-testid={`ai-evidence-badge-${file.id}`}
                  title={`AI-generated · ${file.ai_model || 'model'} · ${file.ai_artifact_type || ''}`}
                >
                  AI · {(file.ai_artifact_type || 'evidence').replace('_', ' ')}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
              {!isPolicy && <><span>{formatFileSize(file.file_size)}</span><span>•</span></>}
              <span>By {file.uploaded_by}</span>
              <span>•</span>
              <span>{formatDate(file.uploaded_at || file.created_at)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {canViewInline(file) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleView(file)}
                disabled={viewing === file.id}
                title="View"
                data-testid={`file-view-btn-${file.id}`}
              >
                {viewing === file.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownload(file.id, file.filename)}
              disabled={downloading === file.id}
              title={isPolicy ? 'Open policy' : 'Download'}
            >
              {downloading === file.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isPolicy ? (
                <ExternalLink className="w-4 h-4" />
              ) : (
                <Download className="w-4 h-4" />
              )}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-600 hover:text-red-700"
                  disabled={deleting === file.id}
                >
                  {deleting === file.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{isPolicy ? 'Unlink policy' : 'Delete File'}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {isPolicy
                      ? `Remove the link to "${file.filename}" from this evidence requirement? The policy itself won't be deleted.`
                      : `Are you sure you want to delete "${file.filename}"? This action cannot be undone.`}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => handleDelete(file.id)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {isPolicy ? 'Unlink' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        );
      })}
    </div>
    <FilePreviewDialog open={!!previewFile} file={previewFile} onClose={() => setPreviewFile(null)} />
    </>
  );
};

export default FileList;
